import axios from 'axios';
import { store, logout, loginSuccess } from '../store';

const BASE_URL = process.env.REACT_APP_API_URL || '/api';
const api = axios.create({ baseURL: BASE_URL, timeout: 180000 });

api.interceptors.request.use(config => {
  const token = store.getState().auth.token;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  res => res,
  async err => {
    const original = err.config;
    if (err.response?.status === 401 && !original._retry) {
      original._retry = true;
      try {
        const refresh = store.getState().auth.refreshToken;
        const { data } = await axios.post(`${BASE_URL}/auth/refresh`, { refresh_token: refresh });
        store.dispatch(loginSuccess(data));
        original.headers.Authorization = `Bearer ${data.access_token}`;
        return api(original);
      } catch {
        store.dispatch(logout());
        window.location.href = '/login';
      }
    }
    return Promise.reject(err);
  }
);

export const authAPI = {
  login:    data => api.post('/auth/login', data),
  register: data => api.post('/auth/register', data),
  logout:   data => api.post('/auth/logout', data),
};

export const portfolioAPI = {
  list:             ()            => api.get('/portfolio').then(r => r.data),
  create:           data          => api.post('/portfolio', data).then(r => r.data),
  update:           (id, data)    => api.put(`/portfolio/${id}`, data).then(r => r.data),
  getHoldings:      id            => api.get(`/portfolio/${id}/holdings`).then(r => r.data),
  addTransaction:   (id, data)    => api.post(`/portfolio/${id}/transactions`, data).then(r => r.data),
  getTransactions:  id            => api.get(`/portfolio/${id}/transactions`).then(r => r.data),
  getPerformance:   id            => api.get(`/portfolio/${id}/performance`).then(r => r.data),
  getAISuggestions: (id, type)    => api.get(`/portfolio/${id}/ai-suggestions?suggestion_type=${type}`).then(r => r.data),
  delete:           id            => api.delete(`/portfolio/${id}`).then(r => r.data),
  getNiches:        ()            => api.get('/portfolio/niches').then(r => r.data),
};

export const marketsAPI = {
  // Live real-time endpoints (Alpha Vantage/Twelve Data/CoinGecko)
  getLiveQuotes: (market, page=1, pageSize=25) =>
    api.get(`/markets/${market}/live?page=${page}&page_size=${pageSize}`)
      .then(r => r.data)
      .catch(() => api.get(`/markets/${market}/quotes?page=${page}&page_size=${pageSize}`).then(r => r.data)),
  getIndices:    market      => api.get(`/markets/${market}/indices`).then(r => r.data),
  getNews:       market      => api.get(`/markets/${market}/news`).then(r => r.data),
  searchStocks:  (q, market) => api.get(`/markets/stocks/search?q=${q}&market=${market}`).then(r => r.data),
};

export const predictionsAPI = {
  analyzeStock:      data           => api.post('/predictions/analyze', data).then(r => r.data),
  getForMarket:      (market, type) => api.get(`/predictions?market=${market}&trade_type=${type}`).then(r => r.data),
  getHistory:        market         => api.get(`/predictions/history${market ? '?market='+market : ''}`).then(r => r.data),
  getAccuracyStats:  ()             => api.get('/predictions/accuracy-stats').then(r => r.data),
  checkPredictions:  ()             => api.post('/predictions/check-predictions').then(r => r.data),
  autoAdjustWeights: ()             => api.post('/predictions/auto-adjust-weights').then(r => r.data),
};

export const settingsAPI = {
  get:          ()       => api.get('/settings').then(r => r.data),
  update:       data     => api.put('/settings', data).then(r => r.data),
  getAIConfigs: ()       => api.get('/settings/ai-apis').then(r => r.data),
  saveAIConfig: data     => api.post('/settings/ai-apis', data).then(r => r.data),
  testAIConfig: provider => api.post(`/settings/ai-apis/${provider}/test`).then(r => r.data),
};

export default api;